import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

import * as authApi from '../api/auth';
import * as usersApi from '../api/users';
import type { CurrentUser, MembershipSummary, Role } from '../api/users';
import { setAccessToken, registerSessionExpiredHandler } from '../api/client';
import { readPinnedTenant } from './tenantPin';
import { useIdleTimeout } from './useIdleTimeout';
import { IdleWarningDialog } from './IdleWarningDialog';

/**
 * Inactivity logout: after IDLE_LIMIT_MS of no interaction we warn for
 * WARN_MS, then sign the user out and bounce to login. Combined with
 * the session-scoped refresh cookie (closing the browser logs you out),
 * this keeps unattended sessions on shared clinical machines short.
 */
const IDLE_LIMIT_MS = 15 * 60 * 1000; // total inactivity before logout
const WARN_MS = 60 * 1000; // how long the warning dialog counts down
const WARN_SECONDS = WARN_MS / 1000;

type SignedOutReason = 'expired' | 'inactivity';

/**
 * Auth context — owns the in-memory access token and the current
 * user. On mount it tries refresh-then-get-me to silently rehydrate
 * a session from the refresh cookie. That keeps the user logged in
 * across page reloads without storing the access token in storage
 * where XSS could read it.
 */

/**
 * `login` returns a discriminated result so the Login page can decide
 * whether to navigate straight to the dashboard (no TOTP) or render
 * the second-step TOTP code input. `completeTotp` does the second
 * step + populates the user state when the code verifies.
 */
export type LoginResult =
  | { kind: 'ready'; firstLogin: boolean }
  | { kind: 'needs_totp'; challenge_token: string; expires_in: number };

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  login(email: string, password: string): Promise<LoginResult>;
  completeTotp(
    challengeToken: string,
    opts: { code?: string; backupCode?: string },
  ): Promise<{ firstLogin: boolean }>;
  logout(): Promise<void>;
  /** Re-fetch /me — call after profile edits or invitation accepts. */
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [signedOutReason, setSignedOutReason] = useState<SignedOutReason | null>(
    null,
  );
  const [idleWarnOpen, setIdleWarnOpen] = useState<boolean>(false);
  const [idleSeconds, setIdleSeconds] = useState<number>(WARN_SECONDS);

  // When a 401 can't be refreshed, the api client calls this: drop the
  // session and raise the "expired" notice. RequireAuth then redirects
  // the now-userless app to /login.
  useEffect(() => {
    registerSessionExpiredHandler(() => {
      setAccessToken(null);
      setUser(null);
      setSignedOutReason('expired');
    });
    return () => registerSessionExpiredHandler(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await usersApi.getMe();
      setUser(me);
    } catch {
      setUser(null);
    }
  }, []);

  // On initial mount: try to lift a session from the HttpOnly refresh
  // cookie. Three outcomes:
  //   - No cookie / expired → /refresh returns null → user stays null
  //   - Refresh succeeds → access token set → /me succeeds → user populated
  //   - /me itself 401s → user stays null
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const tokenResp = await authApi.refresh();
      if (cancelled) return;
      if (tokenResp) {
        await refresh();
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      setSignedOutReason(null); // clear any stale notice on a fresh sign-in
      const resp = await authApi.login(email, password);
      if (authApi.isTotpChallenge(resp)) {
        return {
          kind: 'needs_totp',
          challenge_token: resp.challenge_token,
          expires_in: resp.expires_in,
        };
      }
      await refresh();
      return { kind: 'ready', firstLogin: resp.first_login === true };
    },
    [refresh],
  );

  const completeTotp = useCallback(
    async (
      challengeToken: string,
      opts: { code?: string; backupCode?: string },
    ) => {
      const resp = await authApi.loginTotp(challengeToken, opts);
      await refresh();
      return { firstLogin: resp.first_login === true };
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setAccessToken(null);
    setUser(null);
  }, []);

  // --- Inactivity logout ------------------------------------------------
  // The idle timer fires at IDLE_LIMIT_MS - WARN_MS and opens the warning
  // dialog; the dialog's own countdown does the final logout. While the
  // dialog is open the idle timer is disabled (enabled=false) so mouse
  // movement can't silently dismiss it.
  const handleIdle = useCallback(() => {
    setIdleSeconds(WARN_SECONDS);
    setIdleWarnOpen(true);
  }, []);

  useIdleTimeout({
    timeoutMs: IDLE_LIMIT_MS - WARN_MS,
    onIdle: handleIdle,
    enabled: user !== null && !idleWarnOpen,
  });

  const idleLogout = useCallback(async () => {
    setIdleWarnOpen(false);
    await logout();
    setSignedOutReason('inactivity');
  }, [logout]);

  const staySignedIn = useCallback(async () => {
    setIdleWarnOpen(false);
    // Renew the access token so the resumed session doesn't 401 on the
    // user's very next action; ignore failure (the 401 path will catch it).
    try {
      await authApi.refresh();
    } catch {
      /* no-op */
    }
  }, []);

  // Tick the dialog countdown once a second while it's open; logout at 0.
  useEffect(() => {
    if (!idleWarnOpen) return;
    const id = window.setInterval(() => {
      setIdleSeconds((s) => {
        if (s <= 1) {
          window.clearInterval(id);
          void idleLogout();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [idleWarnOpen, idleLogout]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, completeTotp, logout, refresh }),
    [user, loading, login, completeTotp, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <IdleWarningDialog
        open={idleWarnOpen}
        secondsLeft={idleSeconds}
        onStay={() => void staySignedIn()}
        onLogout={() => void idleLogout()}
      />
      <Snackbar
        open={signedOutReason !== null}
        autoHideDuration={6000}
        onClose={() => setSignedOutReason(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSignedOutReason(null)}
        >
          {signedOutReason === 'inactivity'
            ? 'You were signed out due to inactivity. Please sign in again.'
            : 'Your session has expired. Please sign in again.'}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}

/**
 * A membership grants its role only when:
 *   - is_active = true (per-user suspend), AND
 *   - for tenant-scoped rows, tenant_is_active = true (ROADMAP #37).
 * Superadmin rows have tenant_is_active = null (no tenant to
 * suspend) and pass the gate.
 *
 * Mirrors `_is_effective_membership` on the backend so the portal's
 * role view doesn't drift from the backend's 403 decisions.
 */
export function isEffectiveMembership(m: MembershipSummary): boolean {
  if (!m.is_active) return false;
  if (m.tenant_is_active === false) return false;
  return true;
}

/**
 * Convenience: does the current user hold the given role?
 * Superadmin (tenant_id NULL row) wildcards every check.
 *
 * Memberships in suspended tenants OR with is_active=false are
 * ignored — the backend treats them as inactive on every route,
 * so the portal hides surfaces that would otherwise lead to a 403.
 */
export function userHasRole(user: CurrentUser | null, role: Role): boolean {
  if (!user) return false;
  for (const m of user.memberships) {
    if (!isEffectiveMembership(m)) continue;
    if (m.role === 'superadmin' && m.tenant_id === null) return true;
    if (m.role === role) return true;
  }
  return false;
}

/**
 * Strict role check — true only if the user holds an active
 * membership row with this exact role.
 *
 * Different from [[userHasRole]]: that one wildcards superadmin onto
 * every role check (so route guards can treat a superadmin as having
 * any role's access). This one doesn't — useful when the UI should
 * surface a control only for a user who *actually* holds that role,
 * e.g. a "Profile" sidebar entry that's only relevant to
 * participants, or a per-role help section.
 */
export function userHoldsExactRole(
  user: CurrentUser | null,
  role: Role,
): boolean {
  if (!user) return false;
  return user.memberships.some(
    (m) => isEffectiveMembership(m) && m.role === role,
  );
}

/** Staff roles that hold an operational tenant context — a
 * tenant_admin or a coordinator (TENANT-VIEWER-ROLE). Used to resolve
 * which tenant's pages to show for a signed-in staffer. */
const STAFF_ROLES: Role[] = ['tenant_admin', 'coordinator'];

/**
 * Returns the tenant_id of the user's "active" staff membership — a
 * tenant_admin OR coordinator grant (TENANT-VIEWER-ROLE folded
 * coordinators in so their operational pages resolve a tenant too).
 *
 * Resolution order:
 *   1. The pinned tenant (set when the user accepts a fresh invite,
 *      or by an explicit switcher we'll add later) — used if the user
 *      still holds an active staff role there.
 *   2. The first active staff membership the API returns.
 *
 * Returns null if no active staff role exists at all. (Name kept for
 * back-compat with its many callers; it now resolves coordinators too.)
 */
export function activeAdminTenantId(user: CurrentUser | null): string | null {
  if (!user) return null;
  const pinned = readPinnedTenant();
  if (pinned) {
    const match = user.memberships.find(
      (m) =>
        isEffectiveMembership(m) &&
        STAFF_ROLES.includes(m.role) &&
        m.tenant_id === pinned,
    );
    if (match) return pinned;
  }
  for (const m of user.memberships) {
    if (!isEffectiveMembership(m)) continue;
    if (STAFF_ROLES.includes(m.role) && m.tenant_id !== null) return m.tenant_id;
  }
  return null;
}

/**
 * Read-only tenant admin gate. Returns true iff the user's effective
 * membership in `tenantId` is a tenant_admin flagged read_only — i.e.
 * they can see the data but must NOT be offered write controls.
 *
 * A superadmin is never read-only, so this returns false for them
 * even when they're viewing a tenant whose own admin is read-only.
 * Mirrors `require_tenant_admin_write` on the backend: same accounts
 * the server would reject with 403 are the ones the UI hides controls
 * from. `tenantId` null (no active admin tenant) → false.
 */
export function isReadOnlyAdmin(
  user: CurrentUser | null,
  tenantId: string | null,
): boolean {
  if (!user || !tenantId) return false;
  // Superadmin wildcard: always write-capable.
  if (
    user.memberships.some(
      (m) =>
        isEffectiveMembership(m) &&
        m.role === 'superadmin' &&
        m.tenant_id === null,
    )
  ) {
    return false;
  }
  const m = user.memberships.find(
    (mm) =>
      isEffectiveMembership(mm) &&
      mm.role === 'tenant_admin' &&
      mm.tenant_id === tenantId,
  );
  return m ? m.read_only === true : false;
}

/**
 * The positive form of [[isReadOnlyAdmin]] — true when the user may
 * perform writes in `tenantId`. Use this to render/enable write
 * controls: `const canWrite = canWriteTenant(user, tenantId)`.
 */
export function canWriteTenant(
  user: CurrentUser | null,
  tenantId: string | null,
): boolean {
  return !isReadOnlyAdmin(user, tenantId);
}

/**
 * True iff the user holds an effective coordinator membership in
 * `tenantId` (TENANT-VIEWER-ROLE). Used to badge the role and to
 * hide administrative controls a coordinator can't use.
 */
export function isCoordinator(
  user: CurrentUser | null,
  tenantId: string | null,
): boolean {
  if (!user || !tenantId) return false;
  return user.memberships.some(
    (m) =>
      isEffectiveMembership(m) &&
      m.role === 'coordinator' &&
      m.tenant_id === tenantId,
  );
}

/**
 * True when the user may perform *operational* writes in `tenantId` —
 * running participants, sessions, procedure-instances and devices.
 * Mirrors the backend `require_tenant_operator_write`: a superadmin, a
 * full-access tenant_admin, or a coordinator. A read-only tenant_admin
 * is denied. Use this to gate operational write controls on pages both
 * admins and coordinators can reach (TENANT-VIEWER-ROLE).
 *
 * The narrower [[canWriteTenant]] still gates *administrative* controls
 * (settings, member management) — those pages aren't reachable by a
 * coordinator, so canWriteTenant remains correct there.
 */
export function canOperateTenant(
  user: CurrentUser | null,
  tenantId: string | null,
): boolean {
  if (!user || !tenantId) return false;
  // Superadmin wildcard.
  if (
    user.memberships.some(
      (m) =>
        isEffectiveMembership(m) &&
        m.role === 'superadmin' &&
        m.tenant_id === null,
    )
  ) {
    return true;
  }
  const m = user.memberships.find(
    (mm) =>
      isEffectiveMembership(mm) &&
      STAFF_ROLES.includes(mm.role) &&
      mm.tenant_id === tenantId,
  );
  if (!m) return false;
  // A read-only tenant_admin is view-only; coordinators have no
  // read-only variant.
  return !(m.role === 'tenant_admin' && m.read_only === true);
}

/**
 * Returns the user's "active" participant membership, or null.
 * Honours [[tenantPin]] the same way activeAdminTenantId does, then
 * falls back to the first active participant membership.
 */
export function activeParticipantMembership(
  user: CurrentUser | null,
): MembershipSummary | null {
  if (!user) return null;
  const pinned = readPinnedTenant();
  if (pinned) {
    const match = user.memberships.find(
      (m) =>
        isEffectiveMembership(m) &&
        m.role === 'participant' &&
        m.tenant_id === pinned,
    );
    if (match) return match;
  }
  for (const m of user.memberships) {
    if (!isEffectiveMembership(m)) continue;
    if (m.role === 'participant' && m.tenant_id !== null) return m;
  }
  return null;
}

/**
 * The user's effective tenant_admin memberships, sorted by tenant
 * name so the topbar switcher menu is stable across renders.
 * Honours the suspended-tenant gate (ROADMAP #37) — suspended
 * tenants don't appear in the switcher.
 */
export function adminTenants(user: CurrentUser | null): MembershipSummary[] {
  if (!user) return [];
  return user.memberships
    .filter(
      (m) =>
        isEffectiveMembership(m) &&
        m.role === 'tenant_admin' &&
        m.tenant_id !== null,
    )
    .slice()
    .sort((a, b) =>
      (a.tenant_name ?? '').localeCompare(b.tenant_name ?? ''),
    );
}

/**
 * The user's effective participant memberships, sorted by tenant
 * name. Symmetric to [[adminTenants]] — drives the topbar tenant
 * switcher for pure-participant users (a user who's also a
 * tenant_admin somewhere falls back to admin context; see TopBar).
 *
 * Note for the switcher UX: a participant's data feeds
 * (/sessions/me, /baselines/me, /cognitive/me, /motion/me) are
 * cross-tenant by design. Switching tenants for a participant
 * updates the topbar title + Dashboard greeting but does NOT
 * filter the data — the feeds aggregate across every membership.
 */
export function participantTenants(
  user: CurrentUser | null,
): MembershipSummary[] {
  if (!user) return [];
  return user.memberships
    .filter(
      (m) =>
        isEffectiveMembership(m) &&
        m.role === 'participant' &&
        m.tenant_id !== null,
    )
    .slice()
    .sort((a, b) =>
      (a.tenant_name ?? '').localeCompare(b.tenant_name ?? ''),
    );
}

/**
 * Names of the user's suspended tenants. Drives the Dashboard
 * banner — multi-tenant users see "Your access to X is suspended"
 * while their other tenants keep working; single-tenant users
 * see it explaining why the page is otherwise empty.
 */
export function suspendedTenantNames(user: CurrentUser | null): string[] {
  if (!user) return [];
  return user.memberships
    .filter(
      (m) =>
        m.is_active &&
        m.tenant_is_active === false &&
        m.tenant_name !== null,
    )
    .map((m) => m.tenant_name as string);
}
